'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import store from 'store';
import debounce from 'lodash.debounce';
import Helmet from 'react-helmet';

import DragDropProvider from '@components/Widgets/DragDropProvider.react';
import MealActionsWrapper from '@components/Planner/MealActionsWrapper.react';
import Header from '@components/Dashboard/Header.react';
import Feed from '@components/Dashboard/Feed.react';
import MembershipExpiredBanner from '@components/Widgets/MembershipExpiredBanner.react';
import GridView from '@components/Dashboard/Grid.react';
import BasicMealsBanner from '@components/Dashboard/BasicMealsBanner.react';
import LoginForm from '@components/Widgets/LoginForm.react';
import LoadingSplash, { SPLASH_ROTATION_DURATION } from '@components/Dashboard/Feed/LoadingSplash.react';
import ProfileDoctorModal from '@components/Dashboard/ProfileDoctorModal.react';
import ProfileChangeModal from '@components/Dashboard/ProfileChangeModal.react';
import MealPrepSteps from '@components/Dashboard/MealPrepSteps.react';
import SunbasketDeadlineReminder from '@components/Dashboard/SunbasketDeadlineReminder.react';
import CustomizerModal from '@pro/components/Modals/CustomizerModal.react';
import PostalCodeSelector from '@components/Groceries/PostalCodeSelector.react';

import GroceryStore from '@stores/GroceryStore';
import GroceryActions from '@actions/GroceryActions';
import MealStore from '@stores/MealStore';
import MealActions from '@actions/MealActions';
import PlanStore from '@stores/PlanStore';
import PlanActions from '@actions/PlanActions';
import UserStore from '@stores/UserStore';
import UserActions from '@actions/UserActions';
import AuthStore from '@stores/AuthStore';
import FulfillmentProviderActions from '@actions/FulfillmentProviderActions';
import FulfillmentProviderStore from '@stores/FulfillmentProviderStore';

import Analytics from '@utils/Analytics';
import { getConfig } from '@utils/Env';
import { getAssetsForMeals, getMealTypesForDate } from '@utils/Meals';
import { getCurrentActiveMealType, clearStoredMealType } from '@utils/MealTypeCalculator';
import { hasFinishedOnboarding } from '@utils/ProfileCompletion';
import { getNextDeadline, getSunbasketSubtotalForMeals } from '@utils/Sunbasket';
import { getStepsForMeals } from '@utils/Tasks';
import { profileKeyComparator } from '@pro/utils/Patients';

// Import new MealType system
import MealTypeProvider from '@components/MealType/MealTypeProvider.react';

import './Dashboard.scss';
import '@components/MealType/MealType.scss';

const botJid = 'eatlove-prep-bot@chat.eatlove.is';

export default class Dashboard extends Component {
    static propTypes = {};

    static defaultProps = {};

    static contextTypes = {
        router: PropTypes.object,
        confirm: PropTypes.func,
        isPro: PropTypes.bool,
    };

    static childContextTypes = {
        // Router for navigation
        router: PropTypes.object,

        // User assets
        user: PropTypes.object,
        isPro: PropTypes.bool,
        recommendations: PropTypes.object,
        preferences: PropTypes.object,
        family: PropTypes.array,
        providers: PropTypes.array,

        // Meal assets
        meals: PropTypes.array,
        orders: PropTypes.array,
        plans: PropTypes.array,
        groceries: PropTypes.array,
        recipes: PropTypes.object,
        details: PropTypes.object,
        foods: PropTypes.object,
        merchants: PropTypes.object,
        brands: PropTypes.object,
        locations: PropTypes.object,
        synced: PropTypes.bool,
        loaded: PropTypes.bool,

        // Route options
        params: PropTypes.object,

        // Scroller options
        scrollToMeal: PropTypes.func,
        changeCurrentMealType: PropTypes.func,
        autopopulate: PropTypes.func,
        populating: PropTypes.bool,

        // Re-trigger sync assets (only if necessary)
        synchronizeAssets: PropTypes.func,

        // Added for PrefsDropdown communication
        onRefreshMealFeed: PropTypes.func,
        inhibitProfileAnalysis: PropTypes.bool,

        // Current meal type for DailyLog
        currentMealType: PropTypes.string,
    };

    constructor(props) {
        super(props);

        // Store references to MealType system for bridge functions
        this.mealTypeSetterRef = null;
        this.syncNavigationRef = null;

        const meals = MealStore.getMeals();
        const orders = MealStore.getOrders();
        const plans = PlanStore.getPlans();
        const groceries = GroceryStore.getGroceries();
        const providers = FulfillmentProviderStore.getFulfillmentProviders();

        const user = UserStore.getUser();
        const { postal_code, family = [], preferences = {} } = user || {};

        const { location } = props;
        const currentView = location.query?.grid ? 'grid' : 'feed';

        // Calculate initial meal type
        const currentMealType = getCurrentActiveMealType(preferences, plans);

        this.state = {
            // User assets
            user,
            preferences,
            family,
            recommendations: UserStore.getRecommendedDailyValues(),
            providers,

            // Meal assets
            meals,
            orders,
            plans,
            groceries,
            recipes: {},
            details: {},
            foods: {},
            merchants: {},
            brands: {},
            locations: {},
            synced: false,
            loaded: false,

            // Other various states
            currentView,
            currentMealType,  // Current meal type for DailyLog
            postal_code,
            isModalOpen: false,
            isPostalCodeModalOpen: false,
            capturePostalCodeWorking: false,
            capturePostalCodeAlert: null,

            // State management for PrefsDropdown communication
            inhibitProfileAnalysis: false,
            autopopulated: false,
            populating: false,
            signupCoachmarkOpen: !!this.props.location?.query?.signup,
            minSplashTimeElapsed: !this.props.location?.query?.signup,

            // Profile change state - separate from modal visibility
            isProfileChanged: false,           // Whether profile has changed (can be set on any page)
            profileChangeReasons: [],
        };

        this.resetInhibitPopulate = debounce(this.resetInhibitPopulate, 20000);
    }


    // Callback for MealType system
    handleMealTypeChange = (mealType, manual, setterRef, syncNavigationRef) => {
        // Store setter reference if provided (first time only)
        if (setterRef && !this.mealTypeSetterRef) {
            this.mealTypeSetterRef = setterRef;
        }

        // Store syncNavigation reference for view toggle bridge
        if (syncNavigationRef) {
            this.syncNavigationRef = syncNavigationRef;
        }

        // Update currentMealType in state for child context
        this.setState({ currentMealType: mealType });
    };

    getChildContext = () => {
        const { params } = this.props;
        const { router } = this.context;
        const {
            user,
            preferences,
            recommendations,
            family,
            meals,
            orders,
            recipes,
            providers,
            plans,
            groceries,
            details,
            foods,
            merchants,
            brands,
            locations,
            synced,
            loaded,
            populating,
            inhibitProfileAnalysis,
            currentMealType,
        } = this.state;

        return {
            params,
            router, // Add router to child context

            // User assets
            user,
            preferences,
            recommendations,
            family,
            providers,

            // Meal assets
            meals,
            orders,
            plans,
            groceries,
            recipes,
            details,
            foods,
            merchants,
            brands,
            locations,
            synced,
            loaded,

            // Scroller actions
            scrollToMeal: this.scrollToMeal,
            changeCurrentMealType: this.changeCurrentMealType,
            autopopulate: this.autopopulate,
            populating,

            synchronizeAssets: this.synchronizeAssets,

            // Added for PrefsDropdown communication
            onRefreshMealFeed: this.onRefreshMealFeed,
            inhibitProfileAnalysis,

            // Current meal type for DailyLog
            currentMealType,
        };
    };

    mealTimer = null;
    splashTimer = null;
    minSplashTimer = null;
    initialAutopopulateInProgress = false;

    componentDidMount = () => {
        const { location } = this.props;
        const { confirm, router } = this.context;
        let { pathname, query, hash } = location || {};

        PlanStore.addChangeListener(this.onPlanStoreChange);
        MealStore.addChangeListener(this.onMealStoreChange);
        UserStore.addChangeListener(this.onUserStoreChange);
        GroceryStore.addChangeListener(this.onGroceryStoreChange);
        FulfillmentProviderStore.addChangeListener(this.onFulfillmentProviderChange);
        setTimeout(() => FulfillmentProviderActions.load(this.state.user?.postal_code));

        this.synchronizeAssets();

        // Ensure minimum splash duration during signup so all 3 messages are shown
        if (location?.query?.signup) {
            this.minSplashTimer = setTimeout(() => {
                this.setState({ minSplashTimeElapsed: true });
                this.minSplashTimer = null;
            }, SPLASH_ROTATION_DURATION);
        }

        // Only proceed with modal display and autopopulate if we're on the meals page
        if (location && location.pathname === '/meals') {
            this.initialAutopopulate();
        }

        // MealTypeProvider now handles automatic timer updates

        if (location.query?.verify) {
            confirm(
                'Thank you, your email address has been verified!',
                () => {
                    delete location.query.verify;
                    router.push({ pathname, query, hash });
                },
                () => false,
                { acceptText: 'OK', rejectText: false }
            );
        }

        // Note: No safety timer for signupCoachmarkOpen - the user controls dismissal
        // via the "Start Your Plan" button. The coachmark renders as long as ?signup
        // is in the URL, which the user can always navigate away from.
    };

    componentDidUpdate = (prevProps) => {
        // When signup coachmark is dismissed (?signup removed from URL), start splash message rotation
        if (prevProps.location?.query?.signup && !this.props.location?.query?.signup) {
            // Force splash visible with message rotation (LoadingSplash resets to message 0 automatically)
            this.setState({ signupCoachmarkOpen: false, showSplash: true });

            if (this.splashTimer) {
                clearTimeout(this.splashTimer);
            }

            // Hide splash after all messages complete their rotation cycle
            this.splashTimer = setTimeout(() => {
                this.setState({ showSplash: false });
                this.splashTimer = null;
            }, SPLASH_ROTATION_DURATION);
        }

        if (prevProps.location?.pathname !== '/meals' && this.props.location?.pathname === '/meals') {
            this.initialAutopopulate();
        }
    };

    componentWillUnmount = () => {
        MealStore.removeChangeListener(this.onMealStoreChange);
        PlanStore.removeChangeListener(this.onPlanStoreChange);
        UserStore.removeChangeListener(this.onUserStoreChange);
        GroceryStore.removeChangeListener(this.onGroceryStoreChange);
        FulfillmentProviderStore.removeChangeListener(this.onFulfillmentProviderChange);

        if (this.mealTimer) {
            clearInterval(this.mealTimer);
            this.mealTimer = null;
        }

        if (this.splashTimer) {
            clearTimeout(this.splashTimer);
            this.splashTimer = null;
        }

        if (this.minSplashTimer) {
            clearTimeout(this.minSplashTimer);
            this.minSplashTimer = null;
        }

    };

    onMealStoreChange = () => {
        const meals = MealStore.getMeals();
        const orders = MealStore.getOrders();
        this.setState({ synced: false, meals, orders }, () => {
            this.synchronizeAssets();
            this.initialAutopopulate();
        });
    };

    onPlanStoreChange = () => {
        const plans = PlanStore.getPlans();

        // Clear stored meal type when plans change to ensure consistency
        clearStoredMealType();

        this.setState({
            plans,
        });
    };

    onUserStoreChange = () => {
        // When user or preferences change, update state and check for profile changes
        const { location } = this.props;
        const prevUser = this.state.user;
        const newUser = UserStore.getUser();

        // Clear stored meal type when user changes to ensure consistency
        clearStoredMealType();

        this.setState({
            user: newUser,
            preferences: UserStore.getPreferences(),
            recommendations: UserStore.getRecommendedDailyValues(),
        }, () => {
            if (!newUser) {
                return;
            }

            // Only if we're on the meals page, consider autopopulate
            if (location?.pathname === '/meals' && !this.state.autopopulated) {
                this.initialAutopopulate();
            }

            if (!prevUser) {
                return;
            }

            const { isProfileChanged, profileChangeReasons } = this.checkIfProfileChanged();
            if (isProfileChanged && !this.state.isProfileChanged) {
                this.setState({isProfileChanged, profileChangeReasons}, () => {
                    Analytics.promptToRegenerateMealFeed({ Reasons: profileChangeReasons });
                });
            }
        });
    };

    onGroceryStoreChange = () => {
        this.setState({ groceries: GroceryStore.getGroceries() });
    };

    onFulfillmentProviderChange = () => {
        this.setState({
            providers: FulfillmentProviderStore.getFulfillmentProviders(),
        });
    };

    initialAutopopulate = async (replace = false, clear_groceries = false) => {
        // Prevent re-entry while already running
        if (this.initialAutopopulateInProgress) {
            return;
        }

        const { location } = this.props;
        const { user, preferences, autopopulated } = this.state;
        const { router } = this.context;

        // First check if the profile has changed regardless of current page
        const { isProfileChanged, profileChangeReasons } = this.checkIfProfileChanged();
        if (isProfileChanged && !this.state.isProfileChanged) {
            this.setState({isProfileChanged, profileChangeReasons}, () => {
                Analytics.promptToRegenerateMealFeed({ Reasons: profileChangeReasons });
            });
        }

        // Are we even ready to run initial autopopulate?
        if (autopopulated || !location || location.pathname != '/meals' || !user || !MealStore.isLoginComplete()) {
            return;
        }

        // Only check on initial load, not when explicitly regenerating
        if (!replace) {
            if (isProfileChanged) {
                return;
            }
        }

        // Show splash for the entire initialAutopopulate sequence
        const splashStart = Date.now();
        this.initialAutopopulateInProgress = true;
        this.setState({ autopopulated: true, showSplash: true });

        try {

        const { capabilities = {}, completed = [], postal_code } = user;

        // If the profile is incomplete, redirect to first page of onboarding. Register
        // reads the same constant to pick the step to land on.
        if (!hasFinishedOnboarding(completed) || !user.preferences) {
            Analytics.incompleteProfile({ Completed: completed });
            //Clear call stack before redirect
            Promise.resolve().then(() => router.push('/new-account'));
            return;
        }

        // Or maybe we are ready to run it, but we shouldn't.
        if (user.inhibit_autopopulate || !capabilities.meal_planner) {
            if (!this.splashTimer) {
                this.setState((prevState) => ({ showSplash: prevState.signupCoachmarkOpen }));
            }
            return;
        }

        // If all meals are turned off, we should not autopopulate
        const hasParticipants = this.hasAnyMealParticipants(user);
        if (!hasParticipants) {
            return;
        }

        store.set('inhibit_profile_analysis', true, new Date().getTime() + 20000);

        // Are there meals for the next 3 weeks/shopping_freq's?
        let periodStart =
            user.start_date && moment(user.start_date).isAfter(moment())
                ? moment(user.start_date)
                : moment();
        let firstPeriodStart = moment(periodStart);
        let periodEnd;
        let endDate = moment().add(3, 'weeks').subtract(1, 'day'),
            firstGeneratedFor = null;
        let splash = true;
        let allMeals = MealStore.getMeals();

        // Do we have any meals in the entire 3 week period?
        const anyMealInEntirePeriod = allMeals.find(
            (m) => periodStart.isSameOrBefore(m.date, 'day') && endDate.isSameOrAfter(m.date, 'day')
                && !m.logged_portion && m.source === 'recommendations'
        );

        if (anyMealInEntirePeriod && !replace) {
            const { profile_key } = user;
            const { isProfileChanged } = profileKeyComparator(user, profile_key);
            const accountChanged =  isProfileChanged;

            if (accountChanged) {
                this.setState({
                    inhibitProfileAnalysis: false,
                    autopopulated: false
                });
                return;
            }
        }

        // Clear ALL future meals and plans upfront when replacing (e.g., eating pattern changed)
        // This ensures the feed immediately shows the new eating pattern structure
        // across all future dates by removing old meals and forcing fallback to preferences.meal_types
        if (replace) {
            // Determine which meals on today should be preserved (already consumed)
            let preserveToday = [];
            if (moment().isSame(firstPeriodStart, 'day')) {
                const todayMeals = allMeals.filter(m => moment(firstPeriodStart).isSame(m.date, 'day'));
                preserveToday = todayMeals.filter(m => m.logged_portion).map(m => m.meal);
            }

            MealActions.mealFeedRegen(preserveToday, firstPeriodStart, endDate);
            PlanActions.mealFeedRegen(firstPeriodStart, endDate);

            // Wait for store changes to propagate and Feed to re-render with cleared state
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        let shouldGoToCurrentMeal = true;
        let populateCount = 0;
        while (periodStart.isSameOrBefore(endDate, "day")) {
            periodEnd = moment(periodStart).add(user.shopping_freq - 1, "day");

            // Do we have any meals in this period?
            const meals = allMeals.filter(
                (m) => periodStart.isSameOrBefore(m.date, 'day') && periodEnd.isSameOrAfter(m.date, 'day')
                    && !m.logged_portion && m.source === 'recommendations'
            );

            if (meals.length <= 0 || replace) {
                const response = await this.autopopulate(
                    periodStart,
                    { splash, replace, clear_groceries, skip_clear_on_replace: replace },
                    0,
                    firstPeriodStart.isSame(periodStart, 'day')
                );
                populateCount++;

                // After first populate call, disable splash for subsequent calls
                if (splash) {
                    splash = false;
                }

                if (!firstGeneratedFor && response) {
                    firstGeneratedFor = moment(periodStart);
                }

                if (response?.meals?.length > 0) {
                    if (shouldGoToCurrentMeal && replace) {
                        try {
                            this.goToCurrentMeal();
                        } catch (err) {
                            // goToCurrentMeal may fail if feed ref is not available
                        }
                        shouldGoToCurrentMeal = false;
                    }
                    // Find the max date
                    const maxDate = moment.max(response.meals.map((m) => moment(m.date)));
                    periodStart = maxDate;

                    // Refresh allMeals to include newly populated meals
                    allMeals = MealStore.getMeals();
                } else if (!response) {
                    return; // error from populate-meals, let the profile doctor handle it
                } else {
                    // A successful but empty response is not a failure. It means there was
                    // nothing left to place in this period - every meal type was covered by
                    // ignore_first, which happens when the schedule is changed late in the day
                    // and the remaining slots have already been passed. Advance to the next
                    // period rather than abandoning the weeks that follow.
                    periodStart = moment(periodEnd);
                }
            }

            if (populateCount === 0 && this.refs.feed?.completeAutopopulate) {
                this.refs.feed.completeAutopopulate(null, replace, splash);
            }

            // and add 1 day
            periodStart.add(1, 'day');
        }

        // Starting from the
        shouldGoToCurrentMeal = true;
        if (preferences.meal_kit_providers?.includes('sunbasket') && firstGeneratedFor) {
            if (!postal_code) {
                this.setState({ isPostalCodeModalOpen: true });
                return;
            }

            // Whats our next deadline? Starting at Sunday,
            periodStart = moment(getNextDeadline(user?.time_zone)).add(4, 'day');
            periodEnd = moment(periodStart).add(1, 'week');

            while (periodStart.isSameOrBefore(endDate, 'day')) {
                const meals = allMeals.filter(
                    (m) => periodStart.isSameOrBefore(m.date, 'day') && periodEnd.isSameOrAfter(m.date, 'day')
                );

                const assets = await getAssetsForMeals(meals);

                // What's our Sunbasket subtotal for this week?
                const subtotal = getSunbasketSubtotalForMeals(meals, assets);

                // We're only going to regenerate w/o meal kits if the week we're evaluated was generated just earlier
                // in this same function call.
                if (periodStart.isSameOrAfter(firstGeneratedFor) && subtotal > 0 && subtotal < 45) {
                    await this.autopopulate(periodStart, { replace: true, inhibit_meal_kits: true, shopping_freq: 7 });

                    if (shouldGoToCurrentMeal && replace) {
                        this.goToCurrentMeal();
                        shouldGoToCurrentMeal = false;
                    }
                }

                // and add 1 week
                periodStart.add(1, 'week');
                periodEnd = moment(periodStart).add(1, 'week');
            }
        }
        } catch (error) {
            // If anything fails in initialAutopopulate, clean up the state
            this.initialAutopopulateInProgress = false;
            // Only reset showSplash if not in post-coachmark message rotation
            if (!this.splashTimer) {
                this.setState((prevState) => ({ showSplash: prevState.signupCoachmarkOpen }));
            }
            throw error; // Re-throw if needed
        } finally {
            this.initialAutopopulateInProgress = false;

            // Ensure minimum splash duration so all rotation messages are shown
            const remaining = SPLASH_ROTATION_DURATION - (Date.now() - splashStart);
            if (remaining > 0) {
                await new Promise(r => setTimeout(r, remaining));
            }

            // Only reset showSplash if not in post-coachmark message rotation
            if (!this.splashTimer) {
                this.setState((prevState) => ({ showSplash: prevState.signupCoachmarkOpen }));
            }
        }
    };

    goToCurrentMeal = () => {
        if (this.refs.feed) {
            this.refs.feed.goToCurrentMeal();
        }
    };

    // populating = false
    inhibitPopulate = false;

    resetInhibitPopulate = () => {
        this.inhibitPopulate = false;
    };

    // Add a safety timeout method to reset stuck states
    resetPopulationState = () => {
        this.setState({
            populating: false,
            showSplash: false,
            inhibitProfileAnalysis: false
        });
    }

    autopopulate = async (dateStart, opts, retryCount = 0, profileDocOnFail = true) => {

        const { location } = this.props;
        const { user, preferences, currentMealType, plans } = this.state;
        const {
            splash = false,
            merge = false,
            clear_groceries = false,
            shopping_freq = null,
            inhibit_meal_kits = false,
            mealNames = null,
            force = false,
            skip_clear_on_replace = false,
        } = opts;

        let { replace = false } = opts;

        const { capabilities = {} } = user;

        if (user.inhibit_autopopulate && !force) {
            throw "inhibited by setting";
        }

        if (UserStore.getDnt()) {
            throw 'inhibited by dnt';
        }

        if (this.inhibitPopulate) {
            throw 'inhibited by recency';
        }

        if (moment().isAfter(dateStart, 'day')) {
            throw 'in the past';
        }

        if (location.pathname !== '/meals') {
            throw 'not on the meal feed';
        }

        if (this.populating) {
            throw 'already populating';
        }

        if (!capabilities.meal_planner) {
            throw 'no meal_planner capability';
        }

        // Never auto-populate in the past or more than four weeks out.
        if (moment().add(4, 'week').isBefore(dateStart)) {
            throw 'too far in future';
        }

        let ignoreFirst = [];
        if (moment().isSame(dateStart, "day")) {
            // When replacing (eating pattern changed), use current preferences as source of truth
            // instead of stale plans which still reflect the old eating pattern
            const dateSpecificMealTypes = replace
                ? preferences.meal_types
                : getMealTypesForDate(dateStart, plans, preferences.meal_types);
            const mealTypeNames = dateSpecificMealTypes.map(mt => mt.name);
            ignoreFirst = mealTypeNames.slice(0, mealTypeNames.indexOf(currentMealType));

            // Add current meal to ignoreFirst if it's logged
            const allMeals = MealStore.getMeals();
            const currentMeal = allMeals.find(m => moment(dateStart).isSame(m.date, 'day') && m.meal === currentMealType);
            if (currentMeal?.logged_portion) {
                ignoreFirst.push(currentMealType);
            }
        }

        const postBody = {
            date_start: dateStart.format('YYYY-MM-DD'),
            ignore_first: ignoreFirst,
        };

        if (merge) {
            postBody.merge = true;
        }

        if (mealNames) {
            postBody.meals = mealNames;
        }

        if (replace) {
            postBody.replace = true;
        }

        if (clear_groceries) {
            postBody.clear_groceries = true;
        }

        if (inhibit_meal_kits) {
            postBody.inhibit_meal_kits = true;
        }

        if (shopping_freq) {
            postBody.shopping_freq = shopping_freq;
        }

        this.populating = true;
        // Don't override showSplash if signup rotation is active
        if (this.splashTimer) {
            this.setState({ populating: true });
        } else {
            this.setState({ showSplash: splash, populating: true });
        }

        if (this.refs.feed?.beginAutopopulate) {
            this.refs.feed.beginAutopopulate(dateStart);
        }

        try {
            const response = await AuthStore.fetch(UserStore.getLinks().populate, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; schema=populate/meals/1' },
                body: JSON.stringify(postBody),
            });
            // Did the user get logged out in the interim? Do nothing.
            if (!UserStore.getUser()) {
                return;
            }

            const { plans, meals, profile_key, nutrition_compliance } = response;

            if (clear_groceries) {
                GroceryActions.mealFeedRegen();
            }

            // First, clear both meals and the grocery list (unless we already cleared upfront)
            if (replace && response?.meals?.length && !skip_clear_on_replace) {
                // Find the max date
                const maxDate = moment.max(response.meals.map((m) => moment(m.date)));

                // Fallback if maxDate is undefined - use shopping frequency from user preferences
                const endDate = maxDate || moment(dateStart).add(user.shopping_freq - 1, 'days');

                MealActions.mealFeedRegen(ignoreFirst, dateStart, endDate);
                PlanActions.mealFeedRegen(dateStart, endDate);
            }

            // Hydrate these items into the meal store and plan store. The context itself will update.
            PlanActions.hydratePlans(plans);
            MealActions.hydrateMeals(meals, [], nutrition_compliance || null);

            if (profile_key) {
                UserActions.hydrateProfileKey(profile_key);
            }

            if (this.refs.feed?.completeAutopopulate) {
                this.refs.feed.completeAutopopulate(response, replace, splash);
            }

            Analytics.autoPopulate(dateStart.format('YYYY-MM-DD'), opts);

            return response;
        } catch (error) {
            // Did the user get logged out in the interim? Do nothing.
            if (!UserStore.getUser() || !profileDocOnFail) {
                return;
            }

            this.setState({ profileDocParams: { error: error?.message, retryCount, dateStart, ...opts } });
        } finally {
            this.populating = false;
            // Only reset showSplash if not in initialAutopopulate sequence AND not in signup rotation
            if (!this.initialAutopopulateInProgress && !this.splashTimer) {
                this.setState({ showSplash: false, populating: false });
            } else {
                this.setState({ populating: false });
            }
        }
    };


    // Prep steps functionality has been moved to MealPrepSteps and SunbasketDeadlineReminder components
    // Message generation and chat updates are now handled by ChatActions.updatePrepMessage
    // This comment will trigger a webpack rebuild

    synchronizeAssets = () => {
        const { meals, user } = this.state;

        getAssetsForMeals(meals).then(({ recipes, details, foods, merchants, brands, locations }) => {
            let steps = null;

            if (MealStore.isLoginComplete()) {
                // find just a list of the next 5 days meals.
                const startDate = moment(), endDate = moment().add(5, 'day');
                const upcomingMeals = meals.filter(
                    (meal) => startDate.isSameOrBefore(meal.date, 'day') && endDate.isSameOrAfter(meal.date, 'day')
                );

                steps = getStepsForMeals(upcomingMeals, recipes, details, foods).filter((step) =>
                    step.date.isSame(moment(), 'day')
                );

                // Steps are now processed by the MealPrepSteps and SunbasketDeadlineReminder components
            }

            let loaded = MealStore.isRangeLoaded(moment(), moment());

            this.setState({
                recipes,
                details,
                foods,
                merchants,
                brands,
                locations,
                steps,
                synced: true,
                loaded,
            });
        });
    };

    onToggleView = () => {
        let { currentView } = this.state;

        currentView = currentView === 'grid' ? 'feed' : 'grid';

        if (currentView === 'grid') {
            Analytics.expandView({ View: 'Grid' });
        } else {
            Analytics.collapseView({ View: 'Single Meal' });
        }

        // When returning from grid to feed, reset navigation state
        // so Feed lands on the current meal type, not a stale one
        if (currentView === 'feed' && this.syncNavigationRef) {
            this.syncNavigationRef();
        }

        this.setState({ currentView });
    };

    // Bridge function for legacy components that still use changeCurrentMealType
    changeCurrentMealType = (mealType) => {
        // Update state for legacy context consumers
        this.setState({ currentMealType: mealType });

        // Call new MealType system if available
        if (this.mealTypeSetterRef) {
            this.mealTypeSetterRef(mealType, true); // true = manual selection
        }

        // Maintain compatibility with Feed component
        if (this.refs.feed && this.refs.feed.setActiveMeal) {
            this.refs.feed.setActiveMeal(moment(), mealType);
        }
    };

    scrollToMeal = (date, mealType) => {
        if (this.refs.feed && this.refs.feed.scrollToMeal) {
            this.refs.feed.scrollToMeal(date, mealType);
        }
    };

    // Helper method to check if user has any meal participants
    hasAnyMealParticipants = (user) => {
        const mealTypes = user?.preferences?.meal_types || [];
        return mealTypes.some(mealType =>
            mealType.participants && mealType.participants.length > 0
        );
    };

    // Check if the profile has changed - doesn't depend on current page
    checkIfProfileChanged = () => {
        const { user, inhibitProfileAnalysis, profileChangeReasons } = this.state;

        if (!user || inhibitProfileAnalysis || this.state.isProfileChanged) {
            return { isProfileChanged: this.state.isProfileChanged, profileChangeReasons };
        }

        // Expired/lapsed members lose the meal_planner capability, so they cannot
        // regenerate the feed. Detecting a mismatch for them only produces a modal whose
        // action silently does nothing (EAT-4055) — the membership banner is the correct
        // call to action on that screen. The key reconciles on their first regen after
        // reactivating.
        if (!user.capabilities?.meal_planner) {
            return { isProfileChanged: false, profileChangeReasons: [] };
        }

        const hasParticipants = this.hasAnyMealParticipants(user);
        if (!hasParticipants) {
            return { isProfileChanged: false, profileChangeReasons: [] };
        }

        const { profile_key } = user;
        const { isProfileChanged, reasons } = profileKeyComparator(user, profile_key);
        const profileHasChanged = isProfileChanged;

        return { isProfileChanged: profileHasChanged, profileChangeReasons: reasons }; // Profile changed
    };

    // Handle keeping meals as planned without regeneration
    handleKeepMeals = () => {
        // Set inhibit flag to prevent immediate re-checking
        this.setState({
            isProfileChanged: false,
            inhibitProfileAnalysis: true,
            showSplash: false,
        });

        // Update the user's profile key to match current profile
        UserActions.syncProfileKey()
            .then(() => {
                // Reset the inhibit flag after sync
                this.setState({
                    isProfileChanged: false,
                    inhibitProfileAnalysis: false,
                    // Also set autopopulated to true to prevent unnecessary regeneration
                    autopopulated: true
                });
            })
            .catch(error => {
                // Still reset the inhibitor to prevent the modal from being blocked permanently
                this.setState({ inhibitProfileAnalysis: false });
            });
    };

    onRefreshMealFeed = (replace = true, clear_groceries = true) => {
        // Reset guards so a new regeneration can start even if one is in progress
        this.initialAutopopulateInProgress = false;
        this.populating = false;
        this.inhibitPopulate = false;

        return new Promise((resolve, reject) => {
            this.setState({
                isProfileChanged: false, // Reset profile changed flag
                autopopulated: false,
                populating: false,
                inhibitProfileAnalysis: true // Set inhibit flag during refresh
            }, async () => {

                try {
                    await this.initialAutopopulate(replace, clear_groceries);
                    resolve();
                } catch (error) {
                    reject(error);
                } finally {
                    // Reset the inhibit flag after initialAutopopulate completes (success or failure)
                    this.setState({ inhibitProfileAnalysis: false });
                }
            });
        });
    };

    closeProfileDoctor = () => {
        // No need to remove from local storage, using React state now
        this.setState({
            inhibitProfileAnalysis: false,
            profileDocParams: null
        });
        this.inhibitPopulate = false;
    };

    renderProfileDoctor = () => {
        let { profileDocParams } = this.state;

        if (!profileDocParams) {
            return;
        }

        const { dateStart, splash, replace, retryCount, error } = profileDocParams;

        const retryPopulate = async () => {
            this.inhibitPopulate = false;

            this.setState({ profileDocParams: null, inhibitProfileAnalysis: true });

            try {
                await this.autopopulate(dateStart, { splash, replace }, retryCount + 1);
            } catch (error) {
                // Error in retryPopulate during renderProfileDoctor
            } finally {
                this.setState({ inhibitProfileAnalysis: false });
            }
        };

        return (
            <ProfileDoctorModal
                retryCount={retryCount}
                closeModal={this.closeProfileDoctor}
                retry={retryPopulate}
                error={error}
            />
        );
    };

    closeCustomizerModal = () => {
        const { router } = this.context;
        const { pathname, query, hash } = this.props.location;

        delete query.pid;
        router.push({ pathname, query, hash });
    };

    closePostalCodeModal = () => {
        this.setState({ isPostalCodeModalOpen: false });
    };

    renderCustomizerModal = () => {
        const { location } = this.props;
        let { query } = location;

        if (!query.pid) {
            return;
        }

        return (
            <CustomizerModal
                uuid={query.pid}
                closeModal={this.closeCustomizerModal}
                showAddToPlanner={true}
                location={location}
            />
        );
    };

    onChangePostalCode = (ev) => {
        const postalCode = ev.target.value;
        this.setState({ postal_code: postalCode });
    };

    onSavePostalCode = async () => {
        const { postal_code } = this.state;
        UserActions.updateSpecificMeta({ postal_code });

        if (!postal_code.length) {
            this.setState({ capturePostalCodeAlert: 'Please enter your postal code' });
            return;
        }

        this.setState({ capturePostalCodeWorking: true });

        const response = await AuthStore.fetch({
            url: getConfig('recipe_api') + '/providers?postal_code=' + (postal_code || ''),
        });
        const sunbasket = response?.elements.find((pv) => pv.provider_key === 'sunbasket' && pv.available);

        if (!sunbasket) {
            this.setState({
                capturePostalCodeWorking: false,
                capturePostalCodeAlert: 'Very sorry but Sunbasket delivery is not available for your postal code.',
            });

            return;
        }

        UserActions.updateSpecificMeta({ postal_code });

        this.setState({ isPostalCodeModalOpen: false, capturePostalCodeWorking: false, capturePostalCodeAlert: null });
    };

    renderPostalCodeModal = () => {
        const { isPostalCodeModalOpen, postal_code, capturePostalCodeWorking, capturePostalCodeAlert } = this.state;

        if (!isPostalCodeModalOpen) {
            return null;
        }

        return (
            <PostalCodeSelector
                isModalOpen={isPostalCodeModalOpen}
                postalCode={postal_code}
                working={capturePostalCodeWorking}
                closeModal={this.closePostalCodeModal}
                onSavePostalCode={this.onSavePostalCode}
                alertMsg={capturePostalCodeAlert}
                modalTitle="Confirm Delivery Postal Code"
                saveButtonText="SUBMIT POSTAL CODE"
                onChangePostalCode={this.onChangePostalCode}
            >
                <p className="p3">
                    To receive personalized recommendations for meal kits, please enter your postal code below.
                </p>
            </PostalCodeSelector>
        );
    };

    render() {
        const {
            user,
            preferences,
            plans,
            meals,
            recipes,
            foods,
            brands,
            groceries,
            synced,
            populating,
            currentView,
            isProfileChanged,
            profileChangeReasons,
            loaded,
            showSplash,
            signupCoachmarkOpen,
            minSplashTimeElapsed,
        } = this.state;
        const { children, location } = this.props;

        if (!user) {
            return (
                <div className="my-account-login">
                    <Helmet title="Please sign-in or register | EatLove" />
                    <LoginForm defaultSignUp={false} />
                </div>
            );
        }

        // meal_planner is required: without it "Change my meals" cannot regenerate anything.
        const displayProfileChangeModal = isProfileChanged && !user.inhibit_autopopulate
            && user.capabilities?.meal_planner && location?.pathname === '/meals';

        // Legacy context already works via getChildContext - we don't need DashboardContext

        // Splash visible while data is loading, explicitly shown, or min splash time hasn't elapsed (signup only)
        const isLoadingOrMinTime = showSplash || !loaded || !minSplashTimeElapsed;
        // Splash open when: loading OR signup coachmark visible
        const isSplashOpen = isLoadingOrMinTime || signupCoachmarkOpen;
        // Messages rotate during loading, but NOT during coachmark (message stays static)
        const shouldRotateMessages = isLoadingOrMinTime && !signupCoachmarkOpen;

        // Render dashboard content (using legacy context from getChildContext)
        const dashboardContent = (
            <div className="dashboard-page">
                <Helmet title="EatLove" />

                <LoadingSplash isOpen={isSplashOpen} shouldRotateMessages={shouldRotateMessages} hideContent={signupCoachmarkOpen} />

                <DragDropProvider>
                    <MealActionsWrapper ref="actions">
                        <section className="feed">
                            <MembershipExpiredBanner />
                            <Header
                                onToggleView={this.onToggleView}
                                currentView={currentView}
                                onRefreshMealFeed={this.onRefreshMealFeed}
                            />

                            {currentView === "grid"
                                ? <GridView isVisible={true} user={user} />
                                : <Feed ref="feed" />
                            }
                        </section>

                        <div className='footer-hill'></div>

                        {loaded ? children : null}
                    </MealActionsWrapper>
                </DragDropProvider>

                {loaded && <BasicMealsBanner />}

                {/* Components that don't render UI but use context to update chat */}
                {loaded && <MealPrepSteps />}
                {loaded && <SunbasketDeadlineReminder />}

                {this.renderProfileDoctor()}
                {this.renderCustomizerModal()}
                {this.renderPostalCodeModal()}

                {displayProfileChangeModal && (
                    <ProfileChangeModal
                        isOpen={true}
                        reasons={profileChangeReasons}
                        onRefreshMealFeed={this.onRefreshMealFeed}
                        onKeepMeals={this.handleKeepMeals}
                    />
                )}
            </div>
        );

        return (
            <MealTypeProvider
                preferences={preferences}
                plans={plans}
                autoTimer={true}
                timerInterval={15 * 60 * 1000} // 15 minutes
                onMealTypeChange={this.handleMealTypeChange}
            >
                {dashboardContent}
            </MealTypeProvider>
        );
    }
}
